import { IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import type { SolicitudDetalle } from "@/lib/types";
import { useMemo, useState } from "react";
import { useAreas } from "@/features/catalogos/hooks/use-catalogos";
import { useCapturarDatosGenerales } from "@/features/datos-generales/hooks/use-capturar-datos-generales";
import { datosGeneralesSchema, type DatosGeneralesFormValues } from "@/features/datos-generales/types";
import { formatIso } from "@/features/datos-generales/utils/iso-date";
import { Button, Card, Group, Heading, Select, Stack, Text, Textarea, Input } from "@/components/ui";
import type { DatosGenerales } from "@/lib/types";
import { PeriodoServicioFields } from "./PeriodoServicioFields";

interface Props {
  solicitudId: string;
  iniciales: DatosGenerales | null;
  soloLectura: boolean;
  onVolver: () => void;
  /** Recibe la solicitud actualizada (p. ej. para saber si el prorrateo fue automático). */
  onSuccess: (solicitud: SolicitudDetalle) => void;
  /** Texto del botón "volver" · por default asume el paso de carga de CFDI. */
  labelVolver?: string;
}

type FormErrors = Partial<Record<keyof DatosGeneralesFormValues, string>>;

/**
 * Form de captura de datos generales · maneja únicamente UI y el form
 * state local (validation con zod, controlado a mano — el hook de
 * form de Mantine se reemplaza por `useState` + `safeParse`, mismo patrón
 * que `LoginForm`). La mutación vive en `useCapturarDatosGenerales` y la
 * navegación se delega al caller vía `onSuccess`.
 */
export function DatosGeneralesForm({ solicitudId, iniciales, soloLectura, onVolver, onSuccess, labelVolver = "Regresar a carga de CFDI" }: Props) {
  const { data: areas } = useAreas();
  const hoyIso = useMemo(() => formatIso(new Date()), []);
  const capturar = useCapturarDatosGenerales(solicitudId, { onSuccess });

  const [values, setValues] = useState<DatosGeneralesFormValues>(
    iniciales
      ? {
          motivoPago: iniciales.motivoPago,
          periodoServicioInicio: iniciales.periodoServicioInicio,
          periodoServicioFin: iniciales.periodoServicioFin,
          comentarios: iniciales.comentarios ?? "",
          areaId: iniciales.areaId,
        }
      : {
          motivoPago: "",
          periodoServicioInicio: hoyIso,
          periodoServicioFin: hoyIso,
          comentarios: "",
          areaId: "",
        }
  );
  const [errors, setErrors] = useState<FormErrors>({});

  const setCampo = <K extends keyof DatosGeneralesFormValues>(campo: K, valor: DatosGeneralesFormValues[K]) => {
    setValues((v) => ({ ...v, [campo]: valor }));
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = datosGeneralesSchema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof DatosGeneralesFormValues;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    capturar.mutate({
      motivoPago: parsed.data.motivoPago,
      periodoServicioInicio: parsed.data.periodoServicioInicio,
      periodoServicioFin: parsed.data.periodoServicioFin,
      comentarios: parsed.data.comentarios?.trim() ? parsed.data.comentarios.trim() : null,
      areaId: parsed.data.areaId,
    });
  };

  return (
    <Card padding="lg">
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <div>
            <Heading order={5}>Información del pago</Heading>
            <Text size="sm" c="dimmed" className="mt-0.5">
              Todos los campos son obligatorios salvo los marcados como opcionales.
            </Text>
          </div>

          <Input
            label="Motivo de pago"
            placeholder="Ej. Servicio de mantenimiento mensual"
            required
            readOnly={soloLectura}
            value={values.motivoPago}
            onChange={(e) => setCampo("motivoPago", e.currentTarget.value)}
            error={errors.motivoPago}
          />

          <PeriodoServicioFields
            inicio={values.periodoServicioInicio}
            fin={values.periodoServicioFin}
            inicioError={errors.periodoServicioInicio}
            finError={errors.periodoServicioFin}
            soloLectura={soloLectura}
            onInicioChange={(iso) => setCampo("periodoServicioInicio", iso)}
            onFinChange={(iso) => setCampo("periodoServicioFin", iso)}
          />

          <Select
            label="Área"
            placeholder="Selecciona un área…"
            options={areas.map((a) => ({ value: a.id, label: a.nombre }))}
            searchable={!soloLectura}
            required
            disabled={soloLectura}
            value={values.areaId}
            onValueChange={(v) => setCampo("areaId", v)}
            error={errors.areaId}
          />

          <Textarea
            label="Comentarios"
            description="Opcional · contexto adicional para los autorizadores."
            rows={3}
            readOnly={soloLectura}
            value={values.comentarios}
            onChange={(e) => setCampo("comentarios", e.currentTarget.value)}
          />

          <Group justify="space-between" className="mt-1">
            <Button type="button" variant="default" leftSection={<IconArrowLeft size={14} />} onClick={onVolver}>
              {labelVolver}
            </Button>
            {!soloLectura && (
              <Button type="submit" rightSection={<IconArrowRight size={14} />} loading={capturar.isPending}>
                {capturar.isPending ? "Guardando…" : "Guardar y continuar"}
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </Card>
  );
}
